import { runTask } from "@nova/webworker-pool"
import { convertOwiWithCsv } from "./convertOwiWithCsv"
import { convertPngWithCsv } from "./convertPngWithCsv"
import { imageFileStem } from "./parseMmaCsv"
import { csvConversionKind, getFlightCsv } from "./registry"

export type CsvConversionResult =
    | {
          status: "ok"
          /** Encoded image bytes: TIFF for NIR/PNG-thermal, JPEG for RGB. */
          buffer: ArrayBuffer
          /** Output file extension without the dot, e.g. "tiff" or "jpg". */
          extension: string
          mimeType: string
      }
    // No matching telemetry row for this image, or the decode/convert failed.
    | { status: "invalidTelemetry" }

/**
 * Converts a single .png (16-bit thermal) or .owi (NIR/RGB) buffer to a
 * georeferenced image using its flight's MMA telemetry. The telemetry row is
 * matched by filename stem; the heavy decode/grade/encode runs in a worker
 * (.owi natively via sensortool, .png via the JS TIFF encoder).
 *
 * Transfers `fileBuffer` to the worker — the caller must not use it afterwards.
 */
export const convertImageBufferWithCsv = async (
    fileName: string,
    fileBuffer: ArrayBuffer,
    flightId: string
): Promise<CsvConversionResult> => {
    const kind = csvConversionKind(fileName)
    if (!kind) return { status: "invalidTelemetry" }

    const csvRow = getFlightCsv(flightId)?.get(imageFileStem(fileName))
    if (!csvRow) return { status: "invalidTelemetry" }

    const converted = await runTask(
        kind === "owi" ? convertOwiWithCsv : convertPngWithCsv,
        { buffer: fileBuffer, csvRow },
        { transfer: [fileBuffer] }
    )
    if (!converted) return { status: "invalidTelemetry" }

    return {
        status: "ok",
        buffer: converted.tiffBuffer,
        extension: converted.extension,
        mimeType: converted.mimeType,
    }
}
